import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { GitBookFrameProps } from '@gitbook/embed/react';
import { GitBookProvider, useGitBook } from '@gitbook/embed/react';
import { Transition } from '@headlessui/react';

import Loader from '~/shared/components/Loader';
import runtimeEnv from '~/shared/utils/runtimeEnv';

import { useHelpDefaults, type HelpDefaults } from './useHelpDefaults';
import { useDatafyTools } from './tools';

const DEFAULT_SITE_URL = runtimeEnv.gitbookSiteURL ?? 'https://docs.datafy.io/';

type OpenOptions = Omit<GitBookFrameProps, 'className'>;

export const DEFAULT_HELP_TABS = ['docs', 'search'] as const;

export const DEFAULT_HELP_OPEN_OPTIONS: OpenOptions = {
  tabs: [...DEFAULT_HELP_TABS],
  /** Native GitBook close button; emits `close` when clicked.
   * Esc and arrow-key handling inside the iframe are controlled by GitBook, not by host iframe focus.
   */
  closeButton: true,
};

function getGitbookSiteURL(): string {
  return runtimeEnv.gitbookSiteURL ?? DEFAULT_SITE_URL;
}

function getSupportPortalURL(): string {
  return runtimeEnv.supportPortalURL ?? 'https://customer.support.datafy.io/';
}

function openSupportPortal(): void {
  window.open(getSupportPortalURL(), '_blank', 'noopener,noreferrer');
}

const CONTACT_SUPPORT_ACTION: NonNullable<GitBookFrameProps['actions']>[number] = {
  icon: 'circle-question',
  label: 'Contact Support',
  onClick: openSupportPortal,
};

const DEFAULT_HELP_ACTIONS: NonNullable<GitBookFrameProps['actions']> = [CONTACT_SUPPORT_ACTION];

function buildHelpFrameProps(
  frameProps: OpenOptions,
  visitor: HelpDefaults['visitor'],
  suggestions: string[],
  tools: GitBookFrameProps['tools']
): GitBookFrameProps {
  return {
    ...frameProps,
    tabs: frameProps.tabs ?? [...DEFAULT_HELP_TABS],
    trademark: frameProps.trademark ?? false,
    closeButton: frameProps.closeButton ?? true,
    actions: frameProps.actions ?? DEFAULT_HELP_ACTIONS,
    suggestions: frameProps.suggestions ?? suggestions,
    visitor: {
      ...visitor,
      ...frameProps.visitor,
      unsignedClaims: {
        ...visitor.unsignedClaims,
        ...frameProps.visitor?.unsignedClaims,
      },
    },
    tools,
    className: 'gitbook-frame',
  };
}

function useFrozenValue<T>(value: T): T {
  const ref = useRef<T | null>(null);
  if (ref.current === null) ref.current = value;
  return ref.current;
}

type HelpControl = {
  open: (options?: OpenOptions) => void;
  close: () => void;
  isOpen: boolean;
};

const HelpContext = createContext<HelpControl | null>(null);

export function useHelp(): HelpControl {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used inside <HelpHost>');
  return ctx;
}

export function DocsProvider({ children }: { children: ReactNode }) {
  const siteURL = useMemo(() => getGitbookSiteURL(), []);
  return <GitBookProvider siteURL={siteURL}>{children}</GitBookProvider>;
}

type HelpGitBookFrameProps = {
  config: GitBookFrameProps;
  isOpen: boolean;
  onGitBookClose?: () => void;
};

/**
 * Custom iframe wrapper: visitor claims live in a stable frame URL (no reload on route
 * change); suggestions and other UI settings are pushed via configure().
 */
// function focusIframeForKeyboard(iframe: HTMLIFrameElement | null): void {
//   iframe?.contentWindow?.focus();
// }

/**
 * GitBook applies tab config via a post-load postMessage and exposes no
 * "configured" acknowledgement event. The embed therefore briefly renders its
 * default view before configure() is applied. We keep the loader up for a short
 * beat after load to mask that flash. Heuristic, not exact.
 */
const CONFIG_SETTLE_MS = 150;

function HelpGitBookFrame({ config, isOpen, onGitBookClose }: HelpGitBookFrameProps) {
  const gitbook = useGitBook();
  const [isWired, setIsWired] = useState(false);
  const [isPainted, setIsPainted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const clientRef = useRef<ReturnType<typeof gitbook.createFrame> | null>(null);
  const closeUnsubscribeRef = useRef<(() => void) | undefined>(undefined);
  const configRef = useRef(config);
  const onGitBookCloseRef = useRef(onGitBookClose);
  configRef.current = config;
  onGitBookCloseRef.current = onGitBookClose;

  const visitorClaimsKey = JSON.stringify(config.visitor?.unsignedClaims ?? {});

  const frameURL = useMemo(
    () =>
      gitbook.getFrameURL({
        visitor: config.visitor,
        colorScheme: config.colorScheme,
      }),
    [gitbook, config.colorScheme, visitorClaimsKey]
  );

  const applyConfiguration = useCallback(() => {
    const client = clientRef.current;
    if (!client) return;

    const snapshot = configRef.current;
    client.configure({
      tabs: snapshot.tabs,
      actions: snapshot.actions,
      greeting: snapshot.greeting,
      suggestions: snapshot.suggestions,
      tools: snapshot.tools,
      closeButton: snapshot.closeButton,
      trademark: snapshot.trademark,
      assistantName: snapshot.assistantName,
      buttons: [{
        icon: 'book',
        label: 'Documentation',
        onClick: () => {
          window.open(getGitbookSiteURL(), '_blank');
        },
      }],
    });
  }, []);

  const suggestionsKey = JSON.stringify(config.suggestions ?? []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    setIsWired(false);
    setIsPainted(false);
    setIsReady(false);
    closeUnsubscribeRef.current?.();
    closeUnsubscribeRef.current = undefined;
    clientRef.current = null;

    clientRef.current = gitbook.createFrame(iframe);
    applyConfiguration();
    closeUnsubscribeRef.current = clientRef.current.on('close', () => {
      onGitBookCloseRef.current?.();
    });
    setIsWired(true);

    return () => {
      closeUnsubscribeRef.current?.();
      closeUnsubscribeRef.current = undefined;
      clientRef.current = null;
    };
  }, [frameURL, gitbook, applyConfiguration]);

  useEffect(() => {
    if (!isOpen || !isWired) return;
    applyConfiguration();
  }, [isOpen, isWired, suggestionsKey, applyConfiguration]);

  // Once the frame is loaded and wired, re-send config so it reaches the now-connected
  // frame, then reveal after a short beat so GitBook's default-tab flash stays hidden.
  useEffect(() => {
    if (!isWired || !isPainted) return;
    applyConfiguration();
    const timer = window.setTimeout(() => setIsReady(true), CONFIG_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [isWired, isPainted, applyConfiguration]);

  const isLoading = !isReady;

  return (
    <div style={helpFrameContainerStyle}>
      {isLoading && (
        <div style={loadingStyle} role="status" aria-live="polite" aria-label="Loading help">
          <Loader variant="sm" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="GitBook"
        src={frameURL}
        width="100%"
        height="100%"
        allow="clipboard-write"
        tabIndex={-1}
        className={config.className}
        onLoad={() => setIsPainted(true)}
        style={{
          visibility: isReady ? 'visible' : 'hidden',
          ...(config.colorScheme ? { colorScheme: config.colorScheme } : undefined),
        }}
      />
    </div>
  );
}

type HelpPanelProps = {
  isOpen: boolean;
  frameProps: OpenOptions;
  onClose: () => void;
};

function HelpPanel({ isOpen, frameProps, onClose }: HelpPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [hasOpenedHelp, setHasOpenedHelp] = useState(false);
  const liveDefaults = useHelpDefaults();
  const tools = useDatafyTools();

  const frozenVisitor = useFrozenValue(liveDefaults.visitor);
  const frozenTools = useFrozenValue(tools);

  const frameConfig = useMemo(
    () =>
      buildHelpFrameProps(frameProps, frozenVisitor, liveDefaults.suggestions, frozenTools),
    [frameProps, frozenVisitor, liveDefaults.suggestions, frozenTools]
  );

  const panelLabel = useMemo(() => {
    if (frameProps.greeting?.title) return frameProps.greeting.title;
    if (frameProps.tabs?.length === 1 && frameProps.tabs[0] === 'search') {
      return 'Search documentation';
    }
    if (frameProps.tabs?.length === 1 && frameProps.tabs[0] === 'assistant') {
      return 'Help assistant';
    }
    return 'Documentation help';
  }, [frameProps.greeting?.title, frameProps.tabs]);

  useEffect(() => {
    if (isOpen) setHasOpenedHelp(true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    return () => {
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    // Capture phase: closes when focus is on the parent page (not inside the iframe).
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  return (
    <div
      className="fixed bottom-4 z-[99999]"
      style={{
        left: 'calc(var(--sidebar-width, 88px) + 8px)',
        width: 'min(420px, calc(100vw - var(--sidebar-width, 88px) - 16px))',
        transition: 'left 300ms ease, width 300ms ease',
      }}
    >
      {/* Inner: open/close animation */}
      <Transition
        show={isOpen}
        unmount={false}
        enter="transition ease-out duration-300"
        enterFrom="transform opacity-0 scale-95 translate-y-[8px]"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-200"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-[8px]"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-hidden={!isOpen}
          aria-label={panelLabel}
          tabIndex={-1}
          className={`flex h-[640px] max-h-[calc(100vh-32px)] flex-col overflow-hidden rounded-[12px] bg-white shadow-modal ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        >
          {hasOpenedHelp ? (
            <HelpGitBookFrame config={frameConfig} isOpen={isOpen} onGitBookClose={onClose} />
          ) : null}
        </div>
      </Transition>
    </div>
  );
}

export function HelpHost({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [frameProps, setFrameProps] = useState<OpenOptions>({});

  const open = useCallback((options?: OpenOptions) => {
    setFrameProps(options ?? {});
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<HelpControl>(
    () => ({ open, close, isOpen }),
    [open, close, isOpen]
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpPanel isOpen={isOpen} frameProps={frameProps} onClose={close} />
    </HelpContext.Provider>
  );
}

const helpFrameContainerStyle: CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const loadingStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'white',
  zIndex: 1,
};
